import { useEffect, useState, useCallback } from "react"
import { getPayoutBatches, approvePayoutBatch, markBatchSubmitted, getPayouts, createPayoutBatch } from "@/services/payout.service"
import { generateNACHAFile, downloadNACHAFile, nextValidACHDate, isValidACHDate } from "@/utils/nacha"
import { useToast } from "@/context/ToastContext"
import { useAuth } from "@/context/AuthContext"
import { Badge } from "@/components/common/Badge"
import { Button } from "@/components/common/Button"
import { Modal, FormGroup, Input } from "@/components/common/Modal"
import { EmptyState } from "@/components/common/EmptyState"
import { LoadingRow, Spinner } from "@/components/common/Spinner"
import { supabase } from "@/lib/supabase"
import {
  RefreshCw, Download, CheckCircle, Send, Plus, AlertTriangle, Building2
} from "lucide-react"
import type { PayoutBatch } from "@/types"

const COMPANY_INFO = {
  companyName:      "ZIMRAH HEALTH",
  companyId:        "1234567890",   // Replace with your IRS EIN (no dashes)
  originatingDfiId: "12345678",     // Replace with your bank routing number first 8 digits
}

export function NACHABatchesPage() {
  const { toast } = useToast()
  const { user }  = useAuth()
  const [batches, setBatches]   = useState<PayoutBatch[]>([])
  const [loading, setLoading]   = useState(true)
  const [acting, setActing]     = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating]     = useState(false)
  const [effectiveDate, setEffectiveDate] = useState(() => nextValidACHDate())
  const [processingPayouts, setProcessingPayouts] = useState<Record<string, unknown>[]>([])
  const [loadingPayouts, setLoadingPayouts] = useState(false)
  const [generateOpen, setGenerateOpen] = useState(false)
  const [generateBatch, setGenerateBatch] = useState<PayoutBatch | null>(null)
  const [generating, setGenerating]       = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getPayoutBatches()
      setBatches(data)
    } catch { toast("Failed to load batches", "error") }
    finally { setLoading(false) }
  }, [toast])

  useEffect(() => { load() }, [load])

  const loadProcessingPayouts = async () => {
    setLoadingPayouts(true)
    try {
      const data = await getPayouts("processing")
      setProcessingPayouts(data as unknown as Record<string, unknown>[])
    } catch { toast("Failed to load processing payouts", "error") }
    finally { setLoadingPayouts(false) }
  }

  const handleCreateBatch = async () => {
    if (!isValidACHDate(effectiveDate)) {
      toast("Selected date is not a valid ACH banking day", "error"); return
    }
    if (processingPayouts.length === 0) {
      toast("No processing payouts to batch", "error"); return
    }
    setCreating(true)
    try {
      const payoutIds = processingPayouts.map(p => p.id as string)
      await createPayoutBatch(
        payoutIds,
        effectiveDate,
        user?.id ?? "",
        COMPANY_INFO.companyName,
        COMPANY_INFO.companyId
      )
      toast(`Batch created with ${payoutIds.length} entries`, "success")
      setCreateOpen(false)
      load()
    } catch (err: unknown) {
      toast(err instanceof Error ? err.message : "Failed to create batch", "error")
    } finally { setCreating(false) }
  }

  const handleApprove = async (batchId: string) => {
    setActing(batchId)
    try {
      await approvePayoutBatch(batchId, user?.id ?? "")
      toast("Batch approved", "success")
      load()
    } catch { toast("Failed to approve batch", "error") }
    finally { setActing(null) }
  }

  const handleGenerateNACHA = async (batch: PayoutBatch) => {
    setGenerating(true)
    try {
      // Fetch the payout entries for this batch with bank account details
      const { data: payouts, error } = await supabase
        .from("payouts")
        .select("*, bank_accounts(*), users(full_name)")
        .eq("batch_id", batch.id)
      if (error) throw error

      const entries = (payouts ?? []).map((p: Record<string, unknown>) => {
        const bank = p.bank_accounts as Record<string, unknown>
        return {
          routingNumber: (bank?.dfi_routing ?? bank?.routing_number_last4 ?? "000000000") as string,
          accountNumber: (bank?.dfi_account_number ?? bank?.account_number_last4 ?? "0000000000") as string,
          accountType:   ((bank?.account_type as string) ?? "checking") as "checking" | "savings",
          amount:        Math.round((p.amount as number) * 100), // convert to cents
          receiverName:  ((p.users as Record<string, unknown>)?.full_name as string ?? "MEMBER").slice(0, 22).toUpperCase(),
          traceId:       (p.id as string).slice(0, 15).replace(/-/g, ""),
          payoutId:      p.id as string,
        }
      })

      const effectiveDateFormatted = batch.effective_date.replace(/-/g, "")

      const result = generateNACHAFile(COMPANY_INFO, {
        entries,
        effectiveDate: effectiveDateFormatted,
        description: "ZIMRAH PAY",
      })

      if (result.errors.length > 0) {
        toast(`NACHA errors: ${result.errors[0]}`, "error")
        console.error("NACHA errors:", result.errors)
        return
      }

      const filename = `ZIMRAH_ACH_${batch.batch_number}_${effectiveDateFormatted}.ach`
      downloadNACHAFile(result.file, filename)

      // Mark as submitted
      await markBatchSubmitted(batch.id)
      toast(`✅ NACHA file downloaded: ${filename}`, "success")
      toast(`${result.entryCount} entries — $${(result.totalAmountCents / 100).toFixed(2)} total`, "info")
      load()
    } catch (err: unknown) {
      toast(err instanceof Error ? err.message : "Failed to generate NACHA file", "error")
    } finally {
      setGenerating(false)
      setGenerateOpen(false)
    }
  }

  return (
    <div className="space-y-5">
      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 flex gap-4">
        <Building2 size={20} className="text-blue-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-blue-900">ACH Payout Workflow</p>
          <p className="text-xs text-blue-700 mt-1 leading-relaxed">
            1. Approve individual payouts in <strong>Payout Queue</strong> → status becomes "Processing"<br />
            2. Create a NACHA batch here from all Processing payouts<br />
            3. Admin approves the batch<br />
            4. Generate the ACH file → downloads as <code>.ach</code><br />
            5. Upload the <code>.ach</code> file to your bank portal → bank settles on the effective date
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex justify-between items-center">
        <div />
        <div className="flex gap-2">
          <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
          <Button variant="primary" icon={<Plus size={14} />}
            onClick={() => { setCreateOpen(true); loadProcessingPayouts() }}>
            Create New Batch
          </Button>
        </div>
      </div>

      {/* Batches table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">NACHA Batches</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                {["Batch Number", "Status", "Entries", "Total Amount", "Effective Date", "Created", "Actions"].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <LoadingRow cols={7} /> :
               batches.length === 0 ? (
                <tr><td colSpan={7}>
                  <EmptyState
                    icon="📁"
                    title="No NACHA batches yet"
                    subtitle="Approve payouts in the Payout Queue, then create a batch here to generate your ACH file."
                    action={
                      <Button variant="primary" icon={<Plus size={14} />}
                        onClick={() => { setCreateOpen(true); loadProcessingPayouts() }}>
                        Create First Batch
                      </Button>
                    }
                  />
                </td></tr>
               ) : batches.map(b => (
                <tr key={b.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4 font-mono text-sm font-bold text-gray-900">{b.batch_number}</td>
                  <td className="px-6 py-4">
                    {b.status === "draft"            ? <Badge variant="muted"    dot>Draft</Badge>            :
                     b.status === "pending_approval" ? <Badge variant="warning"  dot>Pending Approval</Badge> :
                     b.status === "approved"         ? <Badge variant="info"     dot>Approved</Badge>         :
                     b.status === "submitted"        ? <Badge variant="success"  dot>Submitted to Bank</Badge>:
                     b.status === "settled"          ? <Badge variant="success"  dot>Settled</Badge>          :
                     <Badge variant="danger" dot>Failed</Badge>}
                  </td>
                  <td className="px-6 py-4 font-bold text-gray-900 tabular-nums">{b.total_entries}</td>
                  <td className="px-6 py-4 font-black text-green-600 tabular-nums text-lg">
                    ${(b.total_amount ?? 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-700">
                    {b.effective_date ? new Date(b.effective_date).toLocaleDateString("en-US", {
                      weekday: "short", month: "short", day: "numeric", year: "numeric"
                    }) : "—"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-400">
                    {new Date(b.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-1 flex-wrap">
                      {b.status === "draft" && (
                        <Button size="sm" variant="primary" icon={<CheckCircle size={13} />}
                          loading={acting === b.id}
                          onClick={() => handleApprove(b.id)}>
                          Approve
                        </Button>
                      )}
                      {b.status === "approved" && (
                        <Button size="sm" variant="success" icon={<Download size={13} />}
                          loading={generating && generateBatch?.id === b.id}
                          onClick={() => { setGenerateBatch(b); setGenerateOpen(true) }}>
                          Generate ACH File
                        </Button>
                      )}
                      {b.status === "submitted" && (
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-green-600">
                          <Send size={13} />
                          Submitted to bank
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
               ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Batch Modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Create NACHA Batch" icon="📁" size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button variant="primary" icon={<Plus size={14} />} loading={creating}
              onClick={handleCreateBatch}>
              Create Batch
            </Button>
          </>
        }
      >
        <FormGroup label="Effective Date" required
          hint="Must be a banking day (no weekends or US federal holidays). Next valid date pre-filled.">
          <Input type="date" value={effectiveDate.replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3")}
            min={new Date().toISOString().slice(0, 10)}
            onChange={e => setEffectiveDate(e.target.value.replace(/-/g, ""))} />
          {!isValidACHDate(effectiveDate) && (
            <div className="flex items-center gap-2 mt-2 text-amber-600">
              <AlertTriangle size={14} />
              <span className="text-xs font-semibold">Not a valid ACH banking day</span>
            </div>
          )}
        </FormGroup>

        <FormGroup label="Processing Payouts to Include">
          {loadingPayouts ? (
            <div className="flex justify-center py-6"><Spinner /></div>
          ) : processingPayouts.length === 0 ? (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-100 text-center">
              <p className="text-sm font-bold text-amber-700">No processing payouts</p>
              <p className="text-xs text-amber-600 mt-1">
                Go to Payout Queue and approve pending payouts first.
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              <div className="flex justify-between text-xs font-bold text-gray-500 px-2 mb-2">
                <span>{processingPayouts.length} payouts</span>
                <span className="text-green-600">
                  Total: ${processingPayouts.reduce((s, p) => s + ((p.amount as number) ?? 0), 0).toFixed(2)}
                </span>
              </div>
              {processingPayouts.map(p => (
                <div key={p.id as string}
                  className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <div>
                    <p className="text-sm font-bold text-gray-900">{p.user_name as string ?? "—"}</p>
                    <p className="text-xs text-gray-400">{p.user_email as string}</p>
                  </div>
                  <p className="font-black text-gray-900 tabular-nums">${((p.amount as number) ?? 0).toFixed(2)}</p>
                </div>
              ))}
            </div>
          )}
        </FormGroup>

        {/* Company info reminder */}
        <div className="p-4 rounded-xl bg-zimrah-50 border border-zimrah-100 mt-2">
          <p className="text-xs font-bold text-zimrah-700 uppercase tracking-wider mb-2">
            ACH Company Configuration
          </p>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {[
              ["Company Name", COMPANY_INFO.companyName],
              ["Company ID", COMPANY_INFO.companyId],
              ["Originating DFI", COMPANY_INFO.originatingDfiId],
            ].map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <span className="text-zimrah-500 font-semibold shrink-0">{k}:</span>
                <span className="font-mono text-zimrah-800">{v}</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-zimrah-500 mt-2">
            Update COMPANY_INFO in NACHABatchesPage.tsx with your real EIN and bank routing prefix.
          </p>
        </div>
      </Modal>

      {/* Generate ACH File Modal */}
      <Modal open={generateOpen} onClose={() => setGenerateOpen(false)}
        title="Generate ACH File" icon="📥" size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setGenerateOpen(false)}>Cancel</Button>
            <Button variant="success" icon={<Download size={14} />}
              loading={generating}
              onClick={() => generateBatch && handleGenerateNACHA(generateBatch)}>
              Download ACH File
            </Button>
          </>
        }
      >
        {generateBatch && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-green-50 border border-green-100">
              <p className="text-sm font-black text-green-900">{generateBatch.batch_number}</p>
              <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                {[
                  ["Entries", generateBatch.total_entries],
                  ["Total Amount", `$${(generateBatch.total_amount ?? 0).toFixed(2)}`],
                  ["Effective Date", generateBatch.effective_date
                    ? new Date(generateBatch.effective_date).toLocaleDateString()
                    : "—"],
                  ["Format", "NACHA PPD"],
                ].map(([k, v]) => (
                  <div key={String(k)}>
                    <p className="text-green-600 font-semibold">{k}</p>
                    <p className="font-black text-green-900">{v}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
              <p className="text-xs font-bold text-blue-700 mb-2">After downloading:</p>
              <ol className="text-xs text-blue-600 space-y-1 list-decimal list-inside">
                <li>Log into your bank portal (online banking or SFTP)</li>
                <li>Navigate to ACH origination / file upload</li>
                <li>Upload the <code className="bg-blue-100 px-1 rounded">.ach</code> file</li>
                <li>Confirm the batch — bank will settle on the effective date</li>
                <li>Return here and the batch will be marked "Submitted"</li>
              </ol>
            </div>

            <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-100">
              <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs font-semibold text-amber-700">
                This will mark the batch as "Submitted to Bank". Ensure all entry details 
                (routing numbers, account numbers) are correct before uploading to your bank.
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}